/**
 * Tipos compartilhados do click-to-call (servidor e widget).
 *
 * Este arquivo não importa nada de Node: pode ser usado no bundle do widget.
 *
 * Fluxo:
 *   1. Um sistema externo faz POST /api/click-to-call { phone, userId } com a
 *      chave de API e recebe o id da tarefa (status "pending").
 *   2. O widget do usuário logado consulta POST /api/click-to-call/poll
 *      periodicamente, informando o estado do operador (presença). Quando o
 *      operador está disponível ou em pausa, o servidor entrega a próxima
 *      tarefa pendente ("dispatched").
 *   3. O widget dispara a ligação manual (makeManualCall) e confirma com
 *      PATCH /api/click-to-call/{id} { status: "calling" } ou "failed".
 *   4. Tarefas não entregues até expiresAt viram "expired"; o sistema externo
 *      pode cancelar com DELETE enquanto estiver pendente ("canceled").
 */

export type ClickToCallStatus = 'pending' | 'dispatched' | 'calling' | 'failed' | 'expired' | 'canceled';

export const CLICK_TO_CALL_TERMINAL_STATUSES: readonly ClickToCallStatus[] = ['calling', 'failed', 'expired', 'canceled'];

export function isTerminalStatus(status: ClickToCallStatus): boolean {
  return CLICK_TO_CALL_TERMINAL_STATUSES.includes(status);
}

/** Registro interno da tarefa (datas em epoch ms). */
export interface ClickToCallTask {
  id: string;
  userId: number;
  userLogin?: string;
  /** Número em E.164 (ex.: +555132488509) - é o que o SDK disca. */
  phone: string;
  /** Número formatado para exibição (ex.: (51) 3248-8509). */
  phoneDisplay: string;
  status: ClickToCallStatus;
  createdAt: number;
  expiresAt: number;
  updatedAt: number;
  dispatchedAt?: number;
  /** Quantas vezes a tarefa foi entregue a um widget. */
  attempts: number;
  error?: string;
  /** Identificação livre do sistema que pediu a ligação. */
  source?: string;
  /** Dados livres do sistema externo (ids de contato, nome...), devolvidos ao widget. */
  metadata?: Record<string, string>;
}

/** Tarefa como aparece nas respostas da API e no evento do widget (datas em ISO 8601). */
export interface ClickToCallTaskPublic {
  id: string;
  userId: number;
  userLogin?: string;
  phone: string;
  phoneDisplay: string;
  status: ClickToCallStatus;
  createdAt: string;
  expiresAt: string;
  updatedAt: string;
  dispatchedAt?: string;
  attempts: number;
  error?: string;
  source?: string;
  metadata?: Record<string, string>;
}

export type PresenceState = 'available' | 'on_break' | 'in_call' | 'after_call' | 'offline' | 'unknown';

export interface UserPresence {
  userId: number;
  login?: string;
  state: PresenceState;
  /** Estado bruto do operador no SDK (idle, onBreak, callInProgress...). */
  operatorState?: string;
  updatedAt: number;
}

export interface ClickToCallPollResponse {
  presence: PresenceState;
  task: ClickToCallTaskPublic | null;
  /** Intervalo sugerido pelo servidor para a próxima consulta. */
  pollIntervalMs: number;
  /** Sem nova consulta dentro deste prazo a presença do usuário vira "offline". */
  presenceTtlSeconds: number;
}

/** Evento disparado em `window` pelo widget quando uma tarefa é recebida (detail = ClickToCallTaskPublic). */
export const CLICK_TO_CALL_EVENT = 'callix:click-to-call';

/**
 * Converte o estado do operador no SDK (`useCallOperatorState().state`) na
 * presença usada pelo click-to-call. União declarada pelo client-sdk-react:
 * starting | startupError | idle | offline | onBreak | callRinging | manualCallSetup |
 * manualCallRinging | manualCallFailed | callInProgress | afterCall.
 */
export function presenceFromOperatorState(state: string | null | undefined): PresenceState {
  if (!state) {
    return 'unknown';
  }
  switch (state) {
    case 'idle':
      return 'available';
    case 'onBreak':
      return 'on_break';
    case 'afterCall':
    case 'manualCallFailed': // o SDK fica aqui até o operador tratar a falha da manual anterior
      return 'after_call';
    case 'offline':
    case 'stopped':
    case 'startupError':
    case 'startup.error':
    case 'networkDown':
      return 'offline';
    case 'starting':
      return 'unknown';
    case 'callRinging':
    case 'manualCallSetup':
    case 'manualCallRinging':
    case 'callInProgress':
      return 'in_call';
    default:
      return /call|ringing|dial/i.test(state) ? 'in_call' : 'unknown';
  }
}

/** Só disponível ou em pausa recebem ligações automáticas (o SDK aceita makeManualCall nesses estados). */
export function canDispatchTo(presence: PresenceState): boolean {
  return presence === 'available' || presence === 'on_break';
}

export const PRESENCE_LABELS: Record<PresenceState, string> = {
  available: 'Disponível',
  on_break: 'Em pausa',
  in_call: 'Em chamada',
  after_call: 'Finalizando a chamada anterior',
  offline: 'Offline (sem discador logado)',
  unknown: 'Desconhecida',
};
